import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { toast } from "react-toastify";

import {
  ModalBlur,
  ModalContainer,
  ModalMainText,
  ModalSecundaryText,
  ModalButtonsContainer,
} from "../../../components/shared/components/generalModal/GeneralModal.style";
import {
  CheckboxLabel,
  CheckboxInput,
  Checkmark,
} from "../../../components/chat/endChatModal/EndChatModal.style";
import Button from "../../../components/shared/components/button/Button.style";
import ButtonComponent from "../../../components/shared/components/button/Button.component";
import DatePicker from "../../../components/datepicker/DatePicker.component";
import TimePicker from "../../../components/timePicker/TimePicker.component";
import { getCurrentDateTimeStrings } from "../../../tools/date-utils";
import { toastErrorConfig } from "../../../components/shared/constants/toaster-config";

/**
 * Modal that allows resuming a paused campaign immediately or rescheduling it.
 *
 * @param {Object}   props
 * @param {boolean}  props.show        - Whether the modal is visible.
 * @param {boolean}  props.isUpdating  - Loading state forwarded to the confirm button.
 * @param {Function} props.onConfirm   - Called with `{ sendAt?: string }`. sendAt format: "YYYY-MM-DDTHH:mm:ss".
 * @param {Function} props.onClose     - Called when the user dismisses the modal.
 */
const ResumeCampaignModal = ({ show, isUpdating, onConfirm, onClose }) => {
  const { date: initialDate, time: initialTime } = getCurrentDateTimeStrings();

  const [reschedule, setReschedule] = useState(false);
  const [date, setDate] = useState(initialDate);
  const [time, setTime] = useState(initialTime);

  // Reset internal state whenever the modal opens
  useEffect(() => {
    if (show) {
      const { date: d, time: t } = getCurrentDateTimeStrings();
      setReschedule(false);
      setDate(d);
      setTime(t);
    }
  }, [show]);

  if (!show) return null;

  const handleConfirm = () => {
    if (!reschedule) {
      onConfirm({});
      return;
    }

    const chosen = new Date(`${date}T${time}`);
    if (chosen <= new Date()) {
      toast.error("La fecha debe ser futura", toastErrorConfig);
      return;
    }

    const sendAt = `${date}T${time}:00`;
    onConfirm({ sendAt });
  };

  const { date: today } = getCurrentDateTimeStrings();

  return ReactDOM.createPortal(
    <>
      <ModalBlur fixed />
      <ModalContainer type="general" fixed>
        <ModalMainText hasMarginTop hasMarginBottom>
          Reanudar campaña
        </ModalMainText>
        <ModalSecundaryText hasMarginTop>
          La fecha de envío original ya pasó. Puedes reanudar la campaña ahora
          mismo o programar una nueva fecha de envío.
        </ModalSecundaryText>

        <div style={{ margin: "0.75rem 2rem" }}>
          <CheckboxLabel>
            Programar nueva fecha de envío
            <CheckboxInput
              type="checkbox"
              checked={reschedule}
              onChange={() => setReschedule((prev) => !prev)}
            />
            <Checkmark checked={reschedule} />
          </CheckboxLabel>
        </div>

        {reschedule && (
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "center",
              gap: "15px",
              margin: "0.5rem 2rem 1rem",
            }}
          >
            <DatePicker
              label="Fecha"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              min={today}
            />
            <TimePicker
              label="Hora"
              value={time}
              onChange={(e) => setTime(e.target.value)}
            />
          </div>
        )}

        <ModalButtonsContainer>
          <Button secondary onClick={onClose} type="button">
            Volver
          </Button>
          <ButtonComponent
            onClick={handleConfirm}
            loading={isUpdating}
            disabled={isUpdating}
            text="Reanudar"
          />
        </ModalButtonsContainer>
      </ModalContainer>
    </>,
    document.body
  );
};

export default ResumeCampaignModal;
